import React, { useState, useEffect } from 'react';
import {
  X,
  Clock,
  Save,
  Info,
  Calendar,
  Sun,
  Moon,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';
import WorkflowStepEditorShell from './WorkflowStepEditorShell';

// Time unit options
const TIME_UNITS = [
  { id: 'minutes', label: 'Minutes', maxValue: 59 },
  { id: 'hours', label: 'Hours', maxValue: 168 }, // 1 week max
  { id: 'days', label: 'Days', maxValue: 365 },
  { id: 'weeks', label: 'Weeks', maxValue: 52 },
];

// Common presets
const WAIT_PRESETS = [
  { label: '15 min', duration: 15, unit: 'minutes' },
  { label: '1 hour', duration: 1, unit: 'hours' },
  { label: '4 hours', duration: 4, unit: 'hours' },
  { label: '1 day', duration: 1, unit: 'days' },
  { label: '2 days', duration: 2, unit: 'days' },
  { label: '3 days', duration: 3, unit: 'days' },
  { label: '1 week', duration: 1, unit: 'weeks' },
  { label: '2 weeks', duration: 2, unit: 'weeks' },
];

const WaitNodeEditor = ({ 
  isOpen, 
  onClose, 
  step, 
  onSave,
  width,
  overlayMode,
  onOverlayDismiss,
  activePanelType = 'wait',
  chromeHeight,
}) => {
  const [config, setConfig] = useState({
    waitType: 'duration', // 'duration' | 'until_time' | 'until_date'
    duration: 1,
    unit: 'days',
    // For until_time
    waitUntilTime: '09:00',
    waitUntilDay: 'any', // 'any' | 'weekday' | 'weekend' | specific day
    // For until_date
    waitUntilDate: '',
    // Options
    skipWeekends: false,
    respectTimezone: true,
  });

  // Initialize from step config
  useEffect(() => {
    if (step?.config) {
      setConfig({
        waitType: step.config.waitType || 'duration',
        duration: step.config.duration || 1,
        unit: step.config.unit || 'days',
        waitUntilTime: step.config.waitUntilTime || '09:00',
        waitUntilDay: step.config.waitUntilDay || 'any',
        waitUntilDate: step.config.waitUntilDate || '',
        skipWeekends: step.config.skipWeekends || false,
        respectTimezone: step.config.respectTimezone !== false,
      });
    }
  }, [step]);

  if (!isOpen) return null;

  const applyPreset = (preset) => {
    setConfig(prev => ({
      ...prev,
      waitType: 'duration',
      duration: preset.duration,
      unit: preset.unit,
    }));
  };

  const getWaitDescription = () => {
    if (config.waitType === 'duration') {
      return `Wait ${config.duration} ${config.unit}${config.duration !== 1 ? '' : ''}`;
    } else if (config.waitType === 'until_time') {
      return `Wait until ${config.waitUntilTime} on ${config.waitUntilDay === 'any' ? 'any day' : config.waitUntilDay}`;
    } else {
      return `Wait until ${config.waitUntilDate || 'specific date'}`;
    }
  };

  const handleSave = () => {
    onSave({
      ...step,
      config: {
        ...config,
        configured: true,
      }
    });
    onClose();
  };

  return (
    <WorkflowStepEditorShell
      isOpen={isOpen}
      width={width}
      overlayMode={overlayMode}
      onOverlayDismiss={onOverlayDismiss}
      activePanelType={activePanelType}
      chromeHeight={chromeHeight}
      className="border-l border-slate-200 bg-white"
      testId="wait-node-editor"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-amber-500 to-amber-600">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
            <Clock size={20} className="text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Wait</h3>
            <p className="text-xs text-amber-100">Configure delay before next step</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
        >
          <X size={18} className="text-white" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {/* Wait Type Tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setConfig(prev => ({ ...prev, waitType: 'duration' }))}
            className={cn(
              'flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors',
              config.waitType === 'duration'
                ? 'bg-amber-50 border-amber-300 text-amber-700'
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            )}
          >
            <Clock size={14} className="inline mr-1.5" />
            Duration
          </button>
          <button
            onClick={() => setConfig(prev => ({ ...prev, waitType: 'until_time' }))}
            className={cn(
              'flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors',
              config.waitType === 'until_time'
                ? 'bg-amber-50 border-amber-300 text-amber-700'
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            )}
          >
            <Sun size={14} className="inline mr-1.5" />
            Until Time
          </button>
          <button
            onClick={() => setConfig(prev => ({ ...prev, waitType: 'until_date' }))}
            className={cn(
              'flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors',
              config.waitType === 'until_date'
                ? 'bg-amber-50 border-amber-300 text-amber-700'
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            )}
          >
            <Calendar size={14} className="inline mr-1.5" />
            Until Date
          </button>
        </div>

        {/* Duration Config */}
        {config.waitType === 'duration' && (
          <>
            {/* Quick Presets */}
            <div className="space-y-2">
              <Label className="text-slate-600">Quick Presets</Label>
              <div className="grid grid-cols-4 gap-2">
                {WAIT_PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    onClick={() => applyPreset(preset)}
                    className={cn(
                      'py-2 px-3 rounded-lg border text-xs font-medium transition-colors',
                      config.duration === preset.duration && config.unit === preset.unit
                        ? 'bg-amber-100 border-amber-300 text-amber-700'
                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                    )}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Duration */}
            <div className="space-y-2">
              <Label className="text-slate-600">Custom Duration</Label>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Input
                    type="number"
                    min="1"
                    max={TIME_UNITS.find(u => u.id === config.unit)?.maxValue || 999}
                    value={config.duration}
                    onChange={(e) => setConfig(prev => ({ 
                      ...prev, 
                      duration: parseInt(e.target.value) || 1 
                    }))}
                    className="bg-white"
                  />
                </div>
                <select
                  value={config.unit}
                  onChange={(e) => setConfig(prev => ({ ...prev, unit: e.target.value }))}
                  className="w-32 h-10 px-3 py-2 rounded-md border border-slate-200 bg-white text-sm"
                >
                  {TIME_UNITS.map((unit) => (
                    <option key={unit.id} value={unit.id}>{unit.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Options */}
            <div className="space-y-3">
              <Label className="text-slate-600">Options</Label>
              <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.skipWeekends}
                  onChange={(e) => setConfig(prev => ({ ...prev, skipWeekends: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                />
                <div>
                  <p className="text-sm font-medium text-slate-900">Skip weekends</p>
                  <p className="text-xs text-slate-500">Wait timer pauses on Sat & Sun</p>
                </div>
              </label>
            </div>
          </>
        )}

        {/* Until Time Config */}
        {config.waitType === 'until_time' && (
          <>
            <div className="space-y-2">
              <Label className="text-slate-600">Wait Until Time</Label>
              <Input
                type="time"
                value={config.waitUntilTime}
                onChange={(e) => setConfig(prev => ({ ...prev, waitUntilTime: e.target.value }))}
                className="bg-white"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-600">On Which Day</Label>
              <select
                value={config.waitUntilDay}
                onChange={(e) => setConfig(prev => ({ ...prev, waitUntilDay: e.target.value }))}
                className="w-full h-10 px-3 py-2 rounded-md border border-slate-200 bg-white text-sm"
              >
                <option value="any">Any day (next occurrence)</option>
                <option value="weekday">Next weekday</option>
                <option value="weekend">Next weekend</option>
                <option value="monday">Monday</option>
                <option value="tuesday">Tuesday</option>
                <option value="wednesday">Wednesday</option>
                <option value="thursday">Thursday</option>
                <option value="friday">Friday</option>
              </select>
            </div>

            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex gap-2">
                <Sun size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">
                  If the specified time has already passed today, the workflow will wait until the next occurrence.
                </p>
              </div>
            </div>
          </>
        )}

        {/* Until Date Config */}
        {config.waitType === 'until_date' && (
          <>
            <div className="space-y-2">
              <Label className="text-slate-600">Wait Until Date</Label>
              <Input
                type="date"
                value={config.waitUntilDate}
                onChange={(e) => setConfig(prev => ({ ...prev, waitUntilDate: e.target.value }))}
                className="bg-white"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-600">At Time (Optional)</Label>
              <Input
                type="time"
                value={config.waitUntilTime}
                onChange={(e) => setConfig(prev => ({ ...prev, waitUntilTime: e.target.value }))}
                className="bg-white"
              />
            </div>

            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex gap-2">
                <Calendar size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">
                  The workflow will wait until the specified date and time. If the date has passed, the step will be skipped.
                </p>
              </div>
            </div>
          </>
        )}

        {/* Timezone Option */}
        <div className="space-y-3">
          <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
            <input
              type="checkbox"
              checked={config.respectTimezone}
              onChange={(e) => setConfig(prev => ({ ...prev, respectTimezone: e.target.checked }))}
              className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
            />
            <div>
              <p className="text-sm font-medium text-slate-900">Respect contact timezone</p>
              <p className="text-xs text-slate-500">Use contact's timezone instead of agency timezone</p>
            </div>
          </label>
        </div>

        {/* Preview */}
        <div className="p-4 bg-slate-100 rounded-xl border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Clock size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="font-medium text-slate-900">{getWaitDescription()}</p>
              <p className="text-xs text-slate-500">
                {config.skipWeekends && 'Skips weekends • '}
                {config.respectTimezone ? 'Contact timezone' : 'Agency timezone'}
              </p>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
          <div className="flex gap-2">
            <Info size={14} className="text-slate-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-slate-500">
              Wait steps pause the workflow for the specified time before proceeding to the next action.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          className="bg-amber-600 hover:bg-amber-700"
        >
          <Save size={16} className="mr-2" />
          Save Step
        </Button>
      </div>
    </WorkflowStepEditorShell>
  );
};

export default WaitNodeEditor;
